import * as React from "react";
import { withStyles } from "src/decorators";
import styled, { css } from "styled-components";
import { Loading } from "../Loading";

const ButtonEl = styled.button<IButtonProps>`
  align-items: center;
  background-color: ${(p) => p.theme.colors.background.primary};
  border: none;
  border-radius: ${(p) => p.theme.dimension.borderRadius[0]}px;
  color: white;
  cursor: pointer;
  display: inline-flex;
  font-size: ${(p) => p.theme.text.size.normal};
  font-family: ${(p) => p.theme.text.fontFamily.demi};
  height: 40px;
  min-height: 40px;
  padding: 0 1rem;
  outline: none;
  transition: all 0.2s ease-in-out;

  ${(props) =>
    props.loading &&
    css`
      cursor: none;
      pointer-events: none;
    `}

  ${(props) =>
    props.stretch &&
    css`
      width: 100%;
    `}

    ${(props) =>
    props.size === "xs" &&
    css`
      font-size: ${(p) => p.theme.text.size.medium};
      height: 24px;
      min-height: 24px;
      padding: 0 ${(p) => p.theme.dimension.spacing.s}px;
    `}

  ${(props) =>
    props.size === "s" &&
    css`
      font-size: ${(p) => p.theme.text.size.medium};
      height: 32px;
      min-height: 32px;
    `}

  ${(props) =>
    props.secondary &&
    css`
      background-color: transparent;
      border: none;
      color: ${props.theme.colors.text.primary};
    `}

  ${(props) =>
    props.nude &&
    css`
      background-color: transparent;
      border: none;
      color: ${props.theme.colors.text.primary};
    `}

  ${(props) =>
    props.outline &&
    css`
      background-color: transparent;
      border: 1px solid ${props.theme.colors.background.primary};
      color: ${props.theme.colors.text.primary};
      :hover {
        background-color: ${props.theme.colors.background.primary};
        color: #fff;
      }
    `}
  
  ${(props) =>
    props.error &&
    css`
      background-color: ${props.theme.colors.background.danger};
    `}
  
  ${(props) =>
    props.error &&
    props.outline &&
    css`
      background-color: transparent;
      border: 1px solid ${props.theme.colors.background.danger};
      color: ${props.theme.colors.text.danger};
    `}
  
  ${(props) =>
    props.warning &&
    css`
      background-color: ${props.theme.colors.background.warning};
    `}

  ${(props) =>
    props.warning &&
    props.outline &&
    css`
      background-color: transparent;
      color: ${props.theme.colors.text.warning};
      border-color: ${props.theme.colors.background.warning};
    `}
  
  ${(props) =>
    props.disabled &&
    css`
      background-color: ${props.theme.colors.background.disabled};
      border-color: ${props.theme.colors.text.disabled};
      color: ${props.theme.colors.text.disabled};
      pointer-events: none;
    `}
`;

const ButtonContentWrapperEl = styled.div<IButtonProps>`
  align-items: center;
  display: inline-flex;
  margin-left: auto;
  margin-right: auto;
  white-space: nowrap;

  ${(p) =>
    p.iconAlign === "right" &&
    css`
      flex-direction: row-reverse;
    `}
`;

const IconWrapperEl = styled.span<IButtonProps>`
  align-items: center;
  display: flex;
  margin-right: 8px;
  min-height: 14px;
  min-width: 14px;

  ${(p) =>
    p.iconAlign === "right" &&
    css`
      margin-left: 8px;
      margin-right: 0;
    `}
`;

export interface IButtonProps extends React.ComponentProps<"button"> {
  outline?: boolean;
  warning?: boolean;
  disabled?: boolean;
  secondary?: boolean;
  nude?: boolean;
  error?: boolean;
  size?: string;
  stretch?: boolean;
  icon?: React.ReactNode;
  iconAlign?: "left" | "right";
  loading?: boolean;
}

export const Button = withStyles(
  ({
    children,
    icon,
    iconAlign,
    type = "button",
    loading,
    ...other
  }: IButtonProps) => {
    const secondaryLoading = !other.outline && !other.secondary && !other.nude;

    const content = loading ? (
      <Loading noMargin secondary={secondaryLoading} />
    ) : (
      <>
        {icon && <IconWrapperEl iconAlign={iconAlign}>{icon}</IconWrapperEl>}
        {children}
      </>
    );

    return (
      <ButtonEl {...(other as any)} type={type}>
        <ButtonContentWrapperEl iconAlign={iconAlign}>
          {content}
        </ButtonContentWrapperEl>
      </ButtonEl>
    );
  }
);
